import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, ClockIcon } from 'lucide-react';
import { Order } from './OrderPlanning';
interface CreateOrderFormProps {
  onSave: (order: Order) => void;
  onCancel: () => void;
  selectedDate: Date;
}
export function CreateOrderForm({
  onSave,
  onCancel,
  selectedDate
}: CreateOrderFormProps) {
  const [orderData, setOrderData] = useState<Omit<Order, 'id'>>({
    recipeId: '',
    recipeName: '',
    quantity: 0,
    unit: 'Tonnen',
    date: selectedDate,
    startTime: '08:00',
    endTime: '10:00',
    status: 'planned',
    line: 'Linie 1'
  });
  const [availableRecipes, setAvailableRecipes] = useState([{
    id: '10632',
    name: 'Gustoria Paella'
  }, {
    id: '10633',
    name: 'Gustoria Risotto'
  }, {
    id: '10634',
    name: 'Gustoria Pasta Bolognese'
  }, {
    id: '10635',
    name: 'Gustoria Lasagne'
  }, {
    id: '10636',
    name: 'Gustoria Spaghetti Carbonara'
  }]);
  // Update date when selectedDate changes
  useEffect(() => {
    setOrderData(prev => ({
      ...prev,
      date: selectedDate
    }));
  }, [selectedDate]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderData.recipeId || orderData.quantity <= 0 || !orderData.startTime || !orderData.endTime) {
      alert('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }
    // Find recipe name based on selected recipeId
    const recipe = availableRecipes.find(r => r.id === orderData.recipeId);
    onSave({
      ...orderData,
      recipeName: recipe?.name || orderData.recipeName
    } as Order);
  };
  const handleRecipeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const recipeId = e.target.value;
    const recipe = availableRecipes.find(r => r.id === recipeId);
    setOrderData({
      ...orderData,
      recipeId,
      recipeName: recipe?.name || ''
    });
  };
  // Calculate estimated raw materials based on quantity
  const calculateRawMaterials = () => {
    if (!orderData.recipeId || orderData.quantity <= 0) return [];
    // Mock calculation - in a real app, this would be based on the actual recipe components
    const mockRawMaterials = [{
      id: 'R061307',
      name: 'MSC Alaska Seelachs',
      quantity: orderData.quantity * 0.25
    }, {
      id: 'R053408',
      name: 'Hähnchen Brust St gebraten',
      quantity: orderData.quantity * 0.15
    }, {
      id: 'R161215',
      name: 'Langkornreis',
      quantity: orderData.quantity * 0.3
    }, {
      id: 'R090533',
      name: 'Erbsen',
      quantity: orderData.quantity * 0.1
    }, {
      id: 'R092017',
      name: 'Tomate',
      quantity: orderData.quantity * 0.05
    }, {
      id: 'R092616',
      name: 'Zwiebeln',
      quantity: orderData.quantity * 0.05
    }, {
      id: 'R091606',
      name: 'Paprika',
      quantity: orderData.quantity * 0.05
    }, {
      id: 'R151501',
      name: 'Rapsöl raffiniert',
      quantity: orderData.quantity * 0.05
    }];
    return mockRawMaterials;
  };
  const estimatedRawMaterials = calculateRawMaterials();
  return <div className="bg-white rounded-xl" data-id="element-234">
      <div className="flex justify-between items-center mb-6" data-id="element-235">
        <div data-id="element-236">
          <h3 className="text-xl font-bold text-gray-800" data-id="element-237">
            Neuen Produktionsauftrag anlegen
          </h3>
          <p className="text-gray-600" data-id="element-238">
            Für{' '}
            {selectedDate.toLocaleDateString('de-DE', {
            weekday: 'long',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })}
          </p>
        </div>
        <div className="flex gap-2" data-id="element-239">
          <button onClick={onCancel} className="flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg transition-colors" data-id="element-240">
            <XIcon size={18} data-id="element-241" />
            <span data-id="element-242">Abbrechen</span>
          </button>
          <button onClick={handleSubmit} className="flex items-center gap-2 bg-[#8DC63F] hover:bg-[#7AB929] text-white px-4 py-2 rounded-lg transition-colors" data-id="element-243">
            <SaveIcon size={18} data-id="element-244" />
            <span data-id="element-245">Speichern</span>
          </button>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-246">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-247">
          <div data-id="element-248">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-249">
              Rezept auswählen <span className="text-red-500" data-id="element-250">*</span>
            </label>
            <select className="w-full p-2 border border-gray-300 rounded-md" value={orderData.recipeId} onChange={handleRecipeChange} required data-id="element-251">
              <option value="" data-id="element-252">-- Rezept auswählen --</option>
              {availableRecipes.map(recipe => <option key={recipe.id} value={recipe.id} data-id="element-253">
                  {recipe.id} - {recipe.name}
                </option>)}
            </select>
          </div>
          
          <div data-id="element-254">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-255">
              Menge (in Tonnen) <span className="text-red-500" data-id="element-256">*</span>
            </label>
            <input type="number" step="0.1" min="0.1" className="w-full p-2 border border-gray-300 rounded-md" value={orderData.quantity || ''} onChange={e => setOrderData({
            ...orderData,
            quantity: parseFloat(e.target.value)
          })} required data-id="element-257" />
          </div>
          
        </div>
        {orderData.recipeId && orderData.quantity > 0 && <div className="mt-8 border-t border-gray-200 pt-6" data-id="element-258">
            <h4 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-259">
              Geschätzte Rohstoffmengen
            </h4>
            <div className="overflow-hidden border border-gray-200 rounded-lg" data-id="element-260">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-261">
                <thead className="bg-gray-50" data-id="element-262">
                  <tr data-id="element-263">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-264">
                      Artikel-Nr.
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-265">
                      Bezeichnung
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-266">
                      Menge (Tonnen)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-267">
                  {estimatedRawMaterials.map(material => <tr key={material.id} className="hover:bg-gray-50" data-id="element-268">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-269">
                        {material.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-270">
                        {material.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-271">
                        {material.quantity.toFixed(3)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-500 mt-3" data-id="element-272">
              * Die Rohstoffmengen werden basierend auf dem Rezept und der
              eingegebenen Produktionsmenge berechnet.
            </p>
          </div>}
      </form>
    </div>;
}